import React, { useState, useEffect } from 'react';
import ReactSlider from 'react-slider';
import './sliderInput.css';

const SliderInput = ({
    label,
    min,
    max,
    unit,
    decimal,
    value,
    onChange,
    id,
    name,
    code,
    p,
    product,
    radioSlider = false,
    presetValues = [],
}) => {
    const snapValues = presetValues;

    const getNearestSnapValue = (val) =>
        snapValues.reduce((prev, curr) =>
            Math.abs(curr - val) < Math.abs(prev - val) ? curr : prev
        );

    const [sliderValue, setSliderValue] = useState(parseFloat(value));
    const [numberInputValue, setNumberInputValue] = useState(parseFloat(value));
    const [isMobile, setIsMobile] = useState(window.innerWidth < 500);
    const [currentProduct, setCurrentProduct] = useState(product);

    // Handle product change
    useEffect(() => {
        if (product !== currentProduct) {
            // Reset to first preset when product changes
            if (radioSlider && presetValues.length > 0) {
                const firstPreset = presetValues[0];
                
                // Update local state
                setSliderValue(firstPreset);
                setNumberInputValue(firstPreset);
                setCurrentProduct(product);

                // Trigger onChange
                const outputValue = formatValueForOutput(firstPreset);
                const finalValue = code ? `${code}^${outputValue}` : outputValue;
                onChange({ target: { name, value: finalValue } });
            }
        }
    }, [product]);

    // Initial render effect for radio slider
    useEffect(() => {
        if (radioSlider && presetValues.length > 0) {
            const firstPreset = presetValues[0];
            
            // Format the output value
            
            const outputValue = p 
                ? (String(firstPreset).includes('.') 
                    ? String(firstPreset).replace('.', '') 
                    : String(firstPreset))
                : firstPreset;
            
            
            // Prepare the final value
            const finalValue = code ? `${code}^${outputValue}` : outputValue;
            
            // Update state and trigger onChange
            setSliderValue(firstPreset);
            setNumberInputValue(firstPreset);
            onChange({ target: { name, value: finalValue } });
        }
    }, []);

    // Handle window resize
    useEffect(() => {
        const checkMobileSize = () => {
            setIsMobile(window.innerWidth < 500);
        };

        window.addEventListener('resize', checkMobileSize);
        return () => window.removeEventListener('resize', checkMobileSize);
    }, []);

    useEffect(() => {
        if (p && value && value > max) {
            const decimalValue = parseFloat(value) / 10;
            setSliderValue(decimalValue);
            setNumberInputValue(decimalValue);
        } else {
            setSliderValue(parseFloat(value));
            setNumberInputValue(parseFloat(value));
        }
    }, [value, p]);

    const handleSliderChange = (newValue) => {
        let updatedValue = decimal ? newValue : Math.floor(newValue);
        if (p) {
            updatedValue = getNearestSnapValue(updatedValue);
        }
        setSliderValue(updatedValue);
        setNumberInputValue(updatedValue);
    };

    const handleSliderRelease = () => {
        const outputValue = formatValueForOutput(sliderValue);
        const finalValue = code ? `${code}^${outputValue}` : outputValue;
        onChange({ target: { name, value: finalValue } });
    };

    // New function to handle radio button selection
    const handleRadioChange = (selectedValue) => {
        const newValue = parseFloat(selectedValue);
        const outputValue = formatValueForOutput(newValue);
        const finalValue = code ? `${code}^${outputValue}` : outputValue;
        
        setSliderValue(newValue);
        setNumberInputValue(newValue);
        onChange({ target: { name, value: finalValue } });
    };

    // Rest of the existing methods remain the same...
    const handleNumberInputChange = (e) => {
        let inputValue = e.target.value;

        const isValid = decimal
            ? /^-?\d*\.?\d*$/.test(inputValue)
            : /^\d*$/.test(inputValue);

        if (isValid) {
            setNumberInputValue(inputValue);
        }
    };

    const handleKeyDown = (e) => {
        const invalidChars = ['e', 'E', '+', '-'];
        if (invalidChars.includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleBlur = () => {
        let newValue = parseFloat(numberInputValue);

        if (isNaN(newValue)) newValue = min;
        if (newValue < min) newValue = min;
        if (newValue > max) newValue = max;

        if (p) {
            newValue = getNearestSnapValue(newValue);
        }

        const outputValue = formatValueForOutput(newValue);
        const finalValue = code ? `${code}^${outputValue}` : outputValue;
        onChange({ target: { name, value: finalValue } });

        setSliderValue(newValue);
        setNumberInputValue(newValue);
    };

    const formatValueForOutput = (value) => {
        if (p) {
            if (String(value).includes('.')) {
                return String(value).replace('.', '');
            }
            //return String(value).concat('0');
            return String(value)
        }
        return value;
    };

    // Render radio slider for mobile when radioSlider is true
    if (radioSlider && isMobile && presetValues.length > 0) {
        return (
            <div className="slider-container radio-slider">
                <div className="uk-child-width-1-1 uk-flex-middle uk-grid uk-grid-small uk-margin-medium-top uk-margin-medium-bottom">
                    <div>
                        <label className="uk-form-label uk-display-block">
                            {label}
                        </label>
                    </div>
                    <div className="radio-slider-wrapper">
                        <div className="radio-button-container">
                            {presetValues.map((preset) => (
                                <label 
                                    key={preset} 
                                    className={`radio-button ${sliderValue === preset ? 'selected' : ''}`}
                                >
                                    <input 
                                        type="radio" 
                                        name={`${name}-radio`}
                                        value={preset}
                                        checked={sliderValue === preset}
                                        onChange={() => handleRadioChange(preset)}
                                        style={{ display: 'none' }}
                                    />
                                    {preset}{unit}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Original slider rendering remains the same
    return (
        <div className="slider-container">
            <div className="uk-child-width-1-1 uk-child-width-1-3@m uk-flex-middle uk-grid uk-grid-small uk-margin-medium-top uk-margin-medium-bottom">
                <div>
                    <label className="uk-form-label uk-display-block">
                        {label}
                    </label>
                </div>
                <div className="uk-width-2-3@m">
                    <div className="slider-wrapper">
                        <div className="slider-input-wrapper-wrapper">
                            <div className="slider-input-wrapper">
                                <ReactSlider
                                    id={id}
                                    min={min}
                                    max={max}
                                    step={decimal ? 0.1 : 1}
                                    value={sliderValue}
                                    onAfterChange={handleSliderRelease}
                                    onChange={handleSliderChange}
                                    className="slider"
                                    thumbClassName="slider-thumb"
                                    trackClassName="slider-track"
                                />
                            </div>
                            <div className='desktop' style={{ width: "100%" }}>
                            </div>
                        </div>

                        <div className="slider-value-input">
                            <input
                                type="number"
                                step={decimal ? 0.1 : 1}
                                value={numberInputValue}
                                onChange={handleNumberInputChange}
                                onBlur={handleBlur}
                                onKeyDown={handleKeyDown}
                                className="uk-input"
                                style={{ marginLeft: '10px', textAlign: 'right' }}
                            />
                            <span className='uk-text-meta' style={{ paddingLeft: '10px', float: 'right' }}>{unit}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SliderInput;